import { Injectable, inject, signal } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { Router, NavigationEnd } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { filter } from 'rxjs';
import { db } from '@database/db';

/** Liste des dossiers (tech, BE, BA), avec ou sans identifiant de technicien — jamais un rapport ouvert. */
const ROUTE_DASHBOARD = /^\/dashboard_(tech|be|ba)(\/[^/]+)?\/?(\?.*)?$/;

@Injectable({ providedIn: 'root' })
export class UpdateService {
  private swUpdate = inject(SwUpdate);
  private router = inject(Router);
  private dialog = inject(MatDialog);

  /** Une nouvelle version est téléchargée et activée ; elle s'appliquera au prochain rechargement. */
  readonly updateReady = signal(false);
  private timerRechargement: ReturnType<typeof setInterval> | null = null;

  init() {
    if (!this.swUpdate.isEnabled) return;

    // Listen for new versions
    this.swUpdate.versionUpdates.subscribe((evt) => {
      if ((evt as VersionReadyEvent).type === 'VERSION_READY') {
        // On activait puis on rechargeait IMMÉDIATEMENT, quoi que l'utilisateur soit en
        // train de faire : un wizard ouvert, une annotation en cours, un import de photos,
        // des transactions pas encore envoyées… et la re-synchronisation au redémarrage
        // écrase les modifications locales non confirmées. Désormais : on active (la
        // version sera prise au prochain chargement) et on ne recharge qu'à un moment sûr.
        this.swUpdate.activateUpdate().then(() => {
          this.updateReady.set(true);
          this.programmerRechargementSur();
          void this.tenterRechargement();
        });
      }
    });

    // Service worker cassé : l'app ne peut plus charger ses ressources, on recharge.
    this.swUpdate.unrecoverable.subscribe(() => location.reload());

    // Periodic checks (e.g., every 30 minutes)
    const intervalMs = 30 * 60 * 1000;
    this.checkForUpdates();
    setInterval(() => this.checkForUpdates(), intervalMs);

    // Also check when app becomes visible (common on iOS resume)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.checkForUpdates();
    });

    window.addEventListener('online', () => this.checkForUpdates());
  }

  async checkForUpdates() {
    try { await this.swUpdate.checkForUpdate(); } catch { /* ignore */ }
  }

  /** Réessaie à chaque navigation et toutes les 60 s tant que la mise à jour n'est pas appliquée. */
  private programmerRechargementSur() {
    if (this.timerRechargement) return;
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => void this.tenterRechargement());
    this.timerRechargement = setInterval(() => void this.tenterRechargement(), 60 * 1000);
  }

  /**
   * Recharge uniquement depuis la liste des dossiers, sans dialog ouvert, et une fois
   * la file de synchronisation vide (transactions et photos). Sinon, on attend.
   */
  private async tenterRechargement(): Promise<void> {
    if (!this.updateReady()) return;
    if (!ROUTE_DASHBOARD.test(this.router.url)) return;
    if (this.dialog.openDialogs.length > 0) return;
    if (document.visibilityState !== 'visible') return;

    try {
      const [transactions, photos] = await Promise.all([
        // Même définition que getSyncStats : au-delà de 5 essais la transaction est
        // bloquée, elle ne partira plus — elle ne doit pas retenir la mise à jour à vie.
        db.table('waitingtransaction').where('tries').below(5).count(),
        db.table('photo').filter(p => p.isSync === false && p.uploadBlocked !== true).count(),
      ]);
      if (transactions > 0 || photos > 0) return;
    } catch {
      // IndexedDB indisponible : on ne prend pas le risque, on attend le prochain essai.
      return;
    }

    console.log('🔄 Mise à jour prête et moment sûr (liste des dossiers, file vide) : rechargement');
    location.reload();
  }

  // Hard reset for stubborn caches (esp. iOS Safari)
  async hardResetCaches() {
    try {
      // Unregister service workers
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map(r => r.unregister()));

      // Clear caches
      const keys = await caches.keys();
      await Promise.all(keys.map(k => caches.delete(k)));

      // Optional: clear storage
    //   try { localStorage.clear(); } catch {}
    //   try { sessionStorage.clear(); } catch {}

      // Reload cleanly
      location.reload();
    } catch {
      // As a fallback, reload anyway
      location.reload();
    }
  }
}
